import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  ProgressBar,
  Badge,
  Button
} from '@shopify/polaris';

/**
 * ProfitGoal component - Shows progress toward profit target
 * Key retention mechanic: merchants see progress toward their goal every visit
 */
export default function ProfitGoal({ stats, shopData, onNavigate }) {
  const targetMargin = shopData?.settings?.profit_target_percent || 25;
  const currentMargin = parseFloat(stats?.net_margin ?? stats?.avg_margin ?? 0);
  const totalRevenue = parseFloat(stats?.total_revenue || 0);
  const totalProfit = parseFloat(stats?.total_profit || 0);
  const targetProfit = totalRevenue * (targetMargin / 100);
  const gapToGoal = Math.max(0, targetProfit - totalProfit);
  const progress = Math.min(100, Math.max(0, (currentMargin / targetMargin) * 100));
  const isOnTarget = currentMargin >= targetMargin;

  // Calculate streak (consecutive days with positive profit)
  const profitStreak = shopData?.settings?.profit_streak || 0;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h2">4. Profit goal tracking</Text>
          <InlineStack gap="200">
            {profitStreak > 0 && (
              <Badge tone="success">{profitStreak} day streak</Badge>
            )}
            <Button variant="plain" size="slim" onClick={() => onNavigate('settings')}>
              Edit
            </Button>
          </InlineStack>
        </InlineStack>

        <BlockStack gap="200">
          <InlineStack align="space-between">
            <Text variant="bodySm" as="span" tone="subdued">
              Current: {currentMargin.toFixed(1)}%
            </Text>
            <Text variant="bodySm" as="span" tone="subdued">
              Target: {targetMargin}%
            </Text>
          </InlineStack>
          <ProgressBar
            progress={progress}
            size="small"
            tone={isOnTarget ? 'success' : progress > 60 ? 'primary' : 'critical'}
          />
        </BlockStack>

        {isOnTarget ? (
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="success">On target</Badge>
            <Text variant="bodySm" as="span" tone="subdued">
              Your net margin is protecting the {targetMargin}% goal
            </Text>
          </InlineStack>
        ) : (
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="warning">{(targetMargin - currentMargin).toFixed(1)}% below target</Badge>
            <Text variant="bodySm" as="span" tone="subdued">
              Close the gap by fixing low-margin products and negative orders first
            </Text>
          </InlineStack>
        )}

        <InlineStack gap="400" wrap={true}>
          <BlockStack gap="100">
            <Text variant="bodySm" as="span" tone="subdued">Goal Score</Text>
            <Text variant="headingLg" as="p" tone={progress >= 80 ? 'success' : progress >= 50 ? undefined : 'critical'}>
              {Math.round(progress)}/100
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text variant="bodySm" as="span" tone="subdued">Gap to Goal</Text>
            <Text variant="headingLg" as="p">
              {gapToGoal > 0
                ? gapToGoal.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
                : '$0'}
            </Text>
          </BlockStack>
          <BlockStack gap="100">
            <Text variant="bodySm" as="span" tone="subdued">Current Net Profit</Text>
            <Text variant="headingLg" as="p">
              {totalProfit.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
            </Text>
          </BlockStack>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
